import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, API_BASE, formatApiError } from "@/lib/api";
import { ChevronLeft, Download, Trash2, Eye, Search, Check, CheckCircle2, Circle } from "lucide-react";
import { useDataLoader } from "./_useDataLoader";

const FORM_TYPES = [
  { v: "", label: "All" },
  { v: "membership", label: "Membership" },
  { v: "contribution", label: "Contribution" },
  { v: "collaboration", label: "Collaboration" },
  { v: "contact", label: "Contact" },
  { v: "research", label: "Research (legacy)" },
  { v: "newsletter", label: "Newsletter" },
];

export default function AdminSubmissions() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setRows(await adminApi.submissions(filter || undefined));
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to load.");
    }
  };

  useDataLoader(load, [filter]);
  useEffect(() => {
    document.title = "BSCA Admin · Submissions";
  }, []);
  useDataLoader(async () => setNote(selected?.admin_note || ""), [selected]);

  /* Client-side search + date filter */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(to).getTime() + 24 * 3600 * 1000 - 1 : null;
    return rows.filter((r) => {
      const p = r.payload || {};
      const blob = [
        p.full_name, p.name, p.email, p.organization, p.message, p.motivation,
        p.country, p.city, p.role, p.research_domain, p.collaboration_type,
      ].filter(Boolean).join(" ").toLowerCase();
      if (term && !blob.includes(term)) return false;
      if (fromTs || toTs) {
        const ts = new Date(r.created_at).getTime();
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }
      return true;
    });
  }, [rows, search, from, to]);

  const exportCsv = async () => {
    try {
      const url = `${API_BASE}/admin/submissions/export.csv${filter ? `?form_type=${filter}` : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bsca-submissions${filter ? "-" + filter : ""}.csv`;
      link.click();
    } catch (e) {
      setErr("Export failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    await adminApi.deleteSubmission(id);
    setSelected(null);
    load();
  };

  const toggleReviewed = async (sub) => {
    const updated = await adminApi.patchSubmission(sub.id, { reviewed: !sub.reviewed });
    setRows((prev) => prev.map((r) => (r.id === sub.id ? updated : r)));
    if (selected && selected.id === sub.id) setSelected(updated);
  };

  const saveNote = async () => {
    if (!selected) return;
    const updated = await adminApi.patchSubmission(selected.id, { admin_note: note });
    setRows((prev) => prev.map((r) => (r.id === selected.id ? updated : r)));
    setSelected(updated);
  };

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link to="/admin/dashboard" className="bsca-link text-xs text-[#4B5563] inline-flex items-center">
            <ChevronLeft className="h-3 w-3 mr-1" /> Dashboard
          </Link>
          <h1 className="mt-3 font-serif text-4xl tracking-tight">Form Submissions</h1>
          <p className="text-[#4B5563] text-sm mt-1">
            {filtered.length} of {rows.length} {rows.length === 1 ? "submission" : "submissions"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select data-testid="filter-form-type" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-white border border-black/10 px-4 py-2.5 text-sm rounded-sm">
            {FORM_TYPES.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="btn-export-csv" className="bsca-lift bg-[#050B14] text-white px-5 py-2.5 text-sm rounded-sm inline-flex items-center">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center bg-white border border-black/10 rounded-sm px-3">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, message…"
            data-testid="search-submissions"
            className="px-2 py-2 text-sm outline-none w-72"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-[#4B5563]">
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="filter-from" className="bg-white border border-black/10 px-2 py-1.5 text-xs rounded-sm" />
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="filter-to" className="bg-white border border-black/10 px-2 py-1.5 text-xs rounded-sm" />
        </div>
      </div>

      {err && <div className="text-red-700 text-sm mb-4">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F6F1] text-left">
              <tr>
                <th className="px-3 py-3"></th>
                <th className="px-3 py-3 bsca-eyebrow text-[#7A5A06] text-[10px]">Type</th>
                <th className="px-3 py-3 bsca-eyebrow text-[#7A5A06] text-[10px]">Name / Email</th>
                <th className="px-3 py-3 bsca-eyebrow text-[#7A5A06] text-[10px]">Received</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-[#9CA3AF]">No submissions match.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`admin-submission-row-${r.id}`} className={r.reviewed ? "bg-[#FBF3D5]/20" : "hover:bg-[#F8F6F1]"}>
                  <td className="px-3 py-3">
                    <button onClick={() => toggleReviewed(r)} data-testid={`btn-toggle-review-${r.id}`} title={r.reviewed ? "Mark as new" : "Mark as reviewed"} className="text-[#4B5563] hover:text-[#7A5A06]">
                      {r.reviewed ? <CheckCircle2 className="h-4 w-4 text-[#D4AF37]" /> : <Circle className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-3"><span className="bsca-eyebrow text-[10px] text-[#7A5A06]">{r.form_type}</span></td>
                  <td className="px-3 py-3">
                    <div className="font-medium truncate max-w-[260px]">{r.payload?.full_name || r.payload?.name || r.payload?.email || "—"}</div>
                    <div className="text-xs text-[#9CA3AF] truncate max-w-[260px]">{r.payload?.email}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right">
                    <button data-testid={`btn-view-${r.id}`} onClick={() => setSelected(r)} className="p-1.5 text-[#4B5563] hover:text-[#050B14]"><Eye className="h-4 w-4" /></button>
                    <button data-testid={`btn-delete-sub-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 text-[#4B5563] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-5 bg-white border border-black/10 p-6">
          {!selected ? (
            <div className="text-center py-20 text-[#9CA3AF] text-sm">Select a submission to view payload.</div>
          ) : (
            <div data-testid="admin-submission-detail">
              <div className="flex items-center justify-between">
                <div>
                  <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">{selected.form_type}</div>
                  <div className="mt-1 text-xs text-[#9CA3AF]">{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <button
                  onClick={() => toggleReviewed(selected)}
                  data-testid="btn-detail-toggle-review"
                  className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border ${selected.reviewed ? "bg-[#FBF3D5] border-[#D4AF37] text-[#7A5A06]" : "border-black/15 text-[#4B5563]"}`}
                >
                  {selected.reviewed ? <Check className="h-3 w-3" /> : null}
                  {selected.reviewed ? "Reviewed" : "Mark as reviewed"}
                </button>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                {Object.entries(selected.payload || {}).map(([k, v]) => (
                  <div key={k} className="border-b border-black/10 pb-2">
                    <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">{k}</div>
                    <div className="mt-1 text-[#1f2937] whitespace-pre-wrap break-words">{String(v)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <div className="bsca-eyebrow text-[#7A5A06] text-[10px] mb-1.5">Internal admin note</div>
                <textarea
                  data-testid="admin-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  placeholder="Add a private note about this submission (not visible publicly)…"
                  className="w-full bg-white border border-black/15 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#D4AF37]"
                />
                <button onClick={saveNote} data-testid="btn-save-note" className="mt-2 text-xs bsca-link inline-flex items-center gap-1 text-[#050B14]">
                  Save note
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
